import type { PriceId, QualifyingStatusEnum } from '@iheartjane/types';
import find from 'lodash-es/find';
import sumBy from 'lodash-es/sumBy';

import { EXTRACT, WEIGHT_OPTIONS } from '../constants';
import type {
  QualifyingMetricsSpecial,
  QualifyingStatusData,
  QualifyingStatusProduct,
  RulesAndConditionsSpecial,
} from './storeSpecialDetailUtils.types';

// parse whether a special allows discounts on required products (aka mix-and-match)
export const allowDiscountsOnRequiredProducts = (
  special?: RulesAndConditionsSpecial
) => {
  if (!special) return false;

  const { special_type } = special;

  if (special_type === 'bundle') {
    return (
      special.conditions?.bundle?.settings
        ?.allow_discounts_on_required_products ?? false
    );
  }

  if (special_type === 'spending_threshold') {
    return (
      special.rules?.settings?.allow_discounts_on_required_products ?? false
    );
  }

  return false;
};

export const getDiscountedWithoutSameQualified = (qualifyingStatusData?: {
  discounted_products: QualifyingStatusProduct[];
  qualified_products: QualifyingStatusProduct[];
  qualifying_status: QualifyingStatusEnum;
}) => {
  return qualifyingStatusData?.discounted_products
    ? qualifyingStatusData.discounted_products
        .filter(
          (product) =>
            !find(qualifyingStatusData.qualified_products, {
              id: product.id,
              price_id: product.price_id,
              count: product.count,
            })
        )
        .map((product) => {
          const qualifiedProductCount = find(
            qualifyingStatusData.qualified_products,
            {
              id: product.id,
              price_id: product.price_id,
            }
          )?.count;

          return qualifiedProductCount
            ? { ...product, count: product.count - qualifiedProductCount }
            : product;
        })
    : [];
};

// price_ids that should be rendered on product cards
export const getPriceIdsForStoreSpecial = (
  special?: RulesAndConditionsSpecial,
  qualifyingStatus?: QualifyingStatusEnum,
  enableEachWeightExpansion?: boolean
): PriceId[] => {
  if (!special) return [];

  const { special_type } = special;

  if (special_type === 'bundle') {
    const isQualified = ['qualified', 'applied'].includes(
      String(qualifyingStatus)
    );

    const weights = isQualified
      ? special.conditions?.bundle?.dependent?.weights
      : special.conditions?.bundle?.independent?.weights;

    // Check if weights contains 'each' and return full list if feature flag is enabled
    if (enableEachWeightExpansion && weights?.includes('each')) {
      return EXTRACT.map(({ id }) => id);
    }

    return weights ?? [];
  }

  if (special_type === 'spending_threshold') {
    const isQualified = ['qualified', 'applied'].includes(
      String(qualifyingStatus)
    );

    const rules = isQualified
      ? special.rules?.discountable
      : special.rules?.qualifier;
    const includesWeights = rules?.includes?.[0]?.weights || [];
    const excludesWeights = rules?.excludes?.[0]?.weights || [];

    return filterIncludesExcludesWeights(
      includesWeights,
      excludesWeights,
      !!enableEachWeightExpansion
    );
  }

  if (special_type === 'bulk_pricing') {
    const bulkWeights = special.conditions?.bulk_pricing?.bulk_weights || [];

    // Check if any bulk weight price_id is 'each' and return full list if feature flag is enabled
    if (
      enableEachWeightExpansion &&
      bulkWeights.some(({ price_id }) => price_id === 'each')
    ) {
      return EXTRACT.map(({ id }) => id);
    }

    return bulkWeights
      .map(({ price_id }) => price_id)
      .filter(Boolean) as PriceId[];
  }

  if (special_type === 'product' || special_type === 'cart_total') {
    const includesWeights = special.rules?.includes?.[0]?.weights || [];
    const excludesWeights = special.rules?.excludes?.[0]?.weights || [];

    return filterIncludesExcludesWeights(
      includesWeights,
      excludesWeights,
      !!enableEachWeightExpansion
    );
  }

  return [];
};

// helper for getPriceIdsForStoreSpecial
function filterIncludesExcludesWeights(
  includesWeights: PriceId[],
  excludesWeights: PriceId[],
  enableEachWeightExpansion: boolean
): PriceId[] {
  if (enableEachWeightExpansion && includesWeights.includes('each')) {
    includesWeights = EXTRACT.map(({ id }) => id);
  }

  if (includesWeights.length > 0) {
    return includesWeights.filter(
      (weight) => !excludesWeights.includes(weight)
    );
  }

  return WEIGHT_OPTIONS.map(({ id }) => id).filter(
    (weight) => !excludesWeights.includes(weight)
  );
}

// calculate remainingQualifiers, remainingDiscountables, and reachedMaxDiscountables for a special based on its type and qualifying status
export const getSpecialQualifyingMetrics = ({
  currentSpecial,
  qualifyingStatus,
}: {
  currentSpecial: QualifyingMetricsSpecial;
  qualifyingStatus?: QualifyingStatusData;
}) => {
  const { special_type } = currentSpecial;

  let remainingQualifiers = 0;
  if (special_type === 'bulk_pricing') {
    remainingQualifiers =
      (qualifyingStatus?.target_weight ?? 0) -
      (qualifyingStatus?.product_weight ?? 0);
  } else if (
    special_type === 'spending_threshold' ||
    special_type === 'cart_total'
  ) {
    remainingQualifiers =
      (currentSpecial.rules?.settings?.cart_threshold ?? 0) -
      (qualifyingStatus?.product_price ?? 0);
  } else if (special_type === 'bundle') {
    remainingQualifiers =
      (currentSpecial.conditions?.bundle?.independent
        ?.threshold_number_of_items_in_cart ?? 0) -
      sumBy(qualifyingStatus?.qualified_products, 'count');
  }

  let maxDiscountedProducts = 0;
  if (special_type === 'spending_threshold') {
    maxDiscountedProducts =
      currentSpecial.rules?.settings?.max_number_of_discounted_products ?? 0;
  } else if (special_type === 'bundle') {
    maxDiscountedProducts =
      currentSpecial.conditions?.bundle?.dependent
        ?.max_number_of_discounted_products ?? 0;
  }

  const remainingDiscountables =
    maxDiscountedProducts -
    sumBy(qualifyingStatus?.discounted_products, 'count');

  const reachedMaxDiscountables =
    ((special_type === 'spending_threshold' || special_type === 'bundle') &&
      remainingDiscountables === 0) ||
    (special_type === 'bulk_pricing' && remainingQualifiers === 0);

  return {
    reachedMaxDiscountables,
    remainingDiscountables,
    remainingQualifiers,
  };
};
